import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { adminApi } from "../../api/admin.api.js";
import { useResource } from "../../hooks/useResource.js";
import { FormShell } from "../../components/forms/FormShell.jsx";
import { FormSection } from "../../components/forms/FormSection.jsx";
import { SlugField } from "../../components/forms/SlugField.jsx";
import { Input } from "../../components/ui/Input.jsx";
import { Textarea } from "../../components/ui/Textarea.jsx";
import { Select } from "../../components/ui/Select.jsx";
import { Switch } from "../../components/ui/Switch.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { PageLoader } from "../../components/ui/Spinner.jsx";
import { ImageUpload } from "../../components/upload/ImageUpload.jsx";
import { RichTextEditor } from "../../components/forms/RichTextEditor.jsx";

const DEFAULTS = {
  title: "",
  slug: "",
  category: "",
  tags: "",
  heroImage: "",
  excerpt: "",
  body: "",
  author: "",
  publishedAt: "",
  status: "draft",
  seo: { title: "", description: "", ogImage: "" },
  isFeatured: false,
  sortOrder: 0,
};

/**
 * Convert a JS Date to a value the <input type="datetime-local"> accepts.
 * The native control wants "YYYY-MM-DDTHH:mm" without seconds or timezone.
 */
function toLocalDateTime(value) {
  if (!value) return "";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function buildPayload(values) {
  const out = { ...values };
  // Tags arrives as a comma-separated string from the form; backend wants string[].
  if (typeof out.tags === "string") {
    out.tags = out.tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }
  if (out.publishedAt) {
    out.publishedAt = new Date(out.publishedAt).toISOString();
  } else {
    delete out.publishedAt;
  }
  if (out.sortOrder !== "") out.sortOrder = Number(out.sortOrder);
  if (!out.slug) delete out.slug;
  return out;
}

export default function BlogFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = id === "new" || !id;

  const { detail, create, update } = useResource("blogs", { toastLabel: "blog post" });
  const { data: existing, isLoading } = detail(isNew ? null : id);

  const categoriesQuery = useQuery({
    queryKey: ["blog-categories", "list-for-form"],
    queryFn: () => adminApi.list("blog-categories", { limit: 50 }),
    staleTime: 5 * 60_000,
  });

  const form = useForm({ defaultValues: DEFAULTS });
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  useEffect(() => {
    if (existing) {
      reset({
        ...DEFAULTS,
        ...existing,
        category:
          typeof existing.category === "object" ? existing.category?._id : existing.category,
        tags: (existing.tags ?? []).join(", "),
        publishedAt: toLocalDateTime(existing.publishedAt),
        seo: existing.seo ?? DEFAULTS.seo,
      });
    }
  }, [existing, reset]);

  useEffect(() => {
    document.title = isNew ? "New Blog Post · Admin" : "Edit Blog Post · Admin";
  }, [isNew]);

  const onSubmit = handleSubmit(async (values) => {
    const payload = buildPayload(values);
    if (isNew) {
      await new Promise((resolve, reject) => {
        create.mutate(payload, {
          onSuccess: (created) => {
            navigate(`/blogs/${created._id}`);
            resolve();
          },
          onError: reject,
        });
      });
    } else {
      await new Promise((resolve, reject) => {
        update.mutate({ id, data: payload }, { onSuccess: resolve, onError: reject });
      });
    }
  });

  if (!isNew && isLoading) return <PageLoader />;

  const watchedTitle = watch("title");
  const watchedSlug = watch("slug");
  const watchedStatus = watch("status");

  return (
    <FormShell
      title={isNew ? "New Blog Post" : `Edit "${existing?.title ?? ""}"`}
      backTo="/blogs"
      backLabel="Back to posts"
      onSubmit={onSubmit}
      primaryAction={
        <Button type="submit" loading={isSubmitting}>
          {isNew ? "Create post" : "Save changes"}
        </Button>
      }
      secondaryAction={
        <Button type="button" variant="secondary" onClick={() => navigate("/blogs")}>
          Cancel
        </Button>
      }
      sidebar={
        <>
          <FormSection title="Status">
            <Select label="Status" {...register("status")}>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="published">Published</option>
            </Select>
            <Input
              type="datetime-local"
              label="Publish date"
              hint={
                watchedStatus === "scheduled"
                  ? "Required — set a future date/time."
                  : watchedStatus === "published"
                    ? "Auto-set to now if blank."
                    : "Stored if you switch to scheduled or published."
              }
              {...register("publishedAt")}
            />
            <Controller
              control={control}
              name="isFeatured"
              render={({ field }) => (
                <Switch
                  label="Featured"
                  hint="Featured posts appear on the homepage strip."
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Input type="number" label="Sort order" {...register("sortOrder")} />
          </FormSection>

          <FormSection title="SEO">
            <Input label="SEO title" {...register("seo.title")} />
            <Textarea label="SEO description" rows={3} {...register("seo.description")} />
            <Input label="OG image path" {...register("seo.ogImage")} />
          </FormSection>
        </>
      }
    >
      <FormSection title="Basics">
        <Input
          label="Title"
          required
          error={errors.title?.message}
          {...register("title", { required: "Title is required" })}
        />
        <SlugField
          value={watchedSlug}
          onChange={(v) => setValue("slug", v)}
          source={watchedTitle}
        />
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Category"
            required
            error={errors.category?.message}
            {...register("category", { required: "Category is required" })}
          >
            <option value="">Select a category</option>
            {categoriesQuery.data?.items?.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </Select>
          <Input label="Author" {...register("author")} />
        </div>
        <Input
          label="Tags"
          hint="Comma-separated. Used for the public tag filter."
          {...register("tags")}
        />
        <Textarea
          label="Excerpt"
          rows={2}
          hint="Max 280 chars. Shown on the blog list cards."
          {...register("excerpt")}
        />
      </FormSection>

      <FormSection title="Hero image">
        <Controller
          control={control}
          name="heroImage"
          render={({ field }) => (
            <ImageUpload
              value={field.value}
              onChange={field.onChange}
              subdir="blogs"
              hint="Recommended 16:9, ≥1600 px wide."
            />
          )}
        />
      </FormSection>

      <FormSection
        title="Body"
        description="Reading time auto-computes from the word count when you save."
      >
        <Controller
          control={control}
          name="body"
          render={({ field }) => (
            <RichTextEditor value={field.value} onChange={field.onChange} subdir="blogs" />
          )}
        />
      </FormSection>
    </FormShell>
  );
}
